import React, { useState } from 'react';
import { PercentileBar, ScoreBar, Sparkline } from './graphics';
import { heatStyle, pillarColour } from './palette';
import { fmtValue, metricHeading, REASON_TEXT } from './format';

// The results grid. One cell per statistic (value with its percentile bar), one Score
// column with a per-row expander for the weighting breakdown, rank, trend sparkline.
export default function ResultsGrid({
  result, catalogue, rows, selected, toggleSelect, heat, sort, setSort, hiddenCols, moreCols,
}) {
  const [open, setOpen] = useState(() => new Set());
  if (!result) return null;
  const metrics = result.metrics.filter((m) => !hiddenCols.has(m.key));
  const pillars = result.weighting?.pillars?.map((p) => p.name) || [];
  const hasScore = !!result.weighting;
  const hasSpark = !!result.sparkline;
  const sortBy = (key) => setSort({ key, dir: sort.key === key && sort.dir === 'desc' ? 'asc' : 'desc' });
  const arrow = (key) => (sort.key === key ? (sort.dir === 'desc' ? ' ▾' : ' ▴') : '');
  const toggleOpen = (tr) => setOpen((s) => { const n = new Set(s); if (n.has(tr)) n.delete(tr); else n.add(tr); return n; });
  const nCols = 2 + (moreCols ? 3 : 0) + metrics.length + (hasScore ? 4 : 0) + (hasSpark ? 1 : 0);

  return (
    <div className="qs-grid-scroll">
      <table className="qs-grid">
        <thead>
          <tr>
            <th className="qs-l qs-sel" />
            <th className={`qs-l qs-sortable${sort.key === 'name' ? ' qs-sorted' : ''}`} onClick={() => sortBy('name')}>Fund{arrow('name')}</th>
            <th className={`qs-l qs-sortable${sort.key === 'sub_asset_class' ? ' qs-sorted' : ''}`} onClick={() => sortBy('sub_asset_class')}>SAC{arrow('sub_asset_class')}</th>
            {moreCols && <th className="qs-l qs-sortable" onClick={() => sortBy('style')}>Style{arrow('style')}</th>}
            {moreCols && <th className="qs-l qs-sortable" onClick={() => sortBy('tilt')}>Tilt{arrow('tilt')}</th>}
            {moreCols && <th className="qs-sortable" onClick={() => sortBy('months_history')}>Months{arrow('months_history')}</th>}
            {metrics.map((m) => {
              const vk = `${m.key}:value`; const pk = `${m.key}:percentile`;
              return (
                <th key={m.key} className={`qs-l qs-hmetric${sort.key === vk || sort.key === pk ? ' qs-sorted' : ''}`}>
                  <button type="button" className="qs-hbtn" onClick={() => sortBy(vk)} title="Sort by value">{metricHeading(m, catalogue)}{arrow(vk)}</button>
                  <button type="button" className="qs-hsub" onClick={() => sortBy(pk)} title="Sort by percentile in sub asset class">pct{arrow(pk)}</button>
                </th>
              );
            })}
            {hasScore && <th className={`qs-l qs-sortable${sort.key === 'score' ? ' qs-sorted' : ''}`} onClick={() => sortBy('score')}>Score · {result.weighting.name}{arrow('score')}</th>}
            {hasScore && <th className={`qs-sortable${sort.key === 'screen_rank' ? ' qs-sorted' : ''}`} onClick={() => sortBy('screen_rank')} title="Rank by score among the funds on screen">Rank{arrow('screen_rank')}</th>}
            {hasScore && <th className={`qs-sortable${sort.key === 'rank' ? ' qs-sorted' : ''}`} onClick={() => sortBy('rank')} title="Rank by score among ALL scored funds in the fund's sub asset class (filters do not change it)">SAC rank{arrow('rank')}</th>}
            {hasScore && <th className="qs-l qs-exp-h" title="Show the weighting breakdown">details</th>}
            {hasSpark && <th className="qs-l" title={`Rolling ${result.sparkline.metric.rolling}m ${result.sparkline.metric.statistic} over ${result.sparkline.metric.period}`}>Trend</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const sel = selected.has(r.tr_code);
            const isOpen = open.has(r.tr_code);
            return (
              <React.Fragment key={r.tr_code}>
                <tr className={`${sel ? 'qs-row-sel' : ''}${r.stale ? ' qs-row-stale' : ''}`}>
                  <td className="qs-l qs-sel">
                    <input type="checkbox" checked={sel} onChange={() => toggleSelect(r.tr_code)} aria-label="select" />
                  </td>
                  <td className="qs-l qs-name" title={`${r.tr_code}${r.apir ? ' · ' + r.apir : ''}\n${r.style || '–'} · ${r.tilt || '–'} · ${r.months_history} months\nBenchmark: ${r.benchmark_name || r.benchmark || '–'}\nFirst month ${r.first_obs || '–'}, last ${r.last_obs || '–'}`}>
                    {r.name || r.tr_code}
                    {r.held && <span className="qs-tag qs-tag-held">held</span>}
                    {r.apl && !r.held && <span className="qs-tag">APL</span>}
                    {r.stale && <span className="qs-tag qs-tag-stale" title={`Last observation ${r.last_obs}; before the as-at month`}>stale</span>}
                  </td>
                  <td className="qs-l qs-sac">{r.sub_asset_class}</td>
                  {moreCols && <td className="qs-l qs-dim">{r.style || '–'}</td>}
                  {moreCols && <td className="qs-l qs-dim">{r.tilt || '–'}</td>}
                  {moreCols && <td className="qs-num qs-dim">{r.months_history}</td>}
                  {metrics.map((m) => {
                    const c = r.metrics[m.key] || {};
                    return (
                      <td key={m.key} className="qs-l qs-cell-metric" style={heatStyle(c.percentile, heat)}
                        title={c.reason ? `No value: ${REASON_TEXT[c.reason] || c.reason}` : `${c.n_months} months, coverage ${c.coverage === null ? '–' : (c.coverage * 100).toFixed(0)}%\ncohort median ${fmtValue(c.cohort_median, m.unit)}`}>
                        <span className="qs-cell-val">
                          {c.value === null || c.value === undefined
                            ? <span className="qs-null">{c.reason ? 'n/a' : '–'}</span>
                            : fmtValue(c.value, m.unit)}
                        </span>
                        <PercentileBar pct={c.percentile} thin={c.thin} n={c.cohort_n} width={48} />
                      </td>
                    );
                  })}
                  {hasScore && <td className="qs-l qs-score"><ScoreBar score={r.score} pillarNames={pillars} /></td>}
                  {hasScore && <td className="qs-num">{r.screen_rank ?? '–'}</td>}
                  {hasScore && (
                    <td className="qs-num qs-dim" title={r.score?.rank_in_cohort ? `${r.score.rank_in_cohort} of ${r.score.cohort_scored} scored ${r.sub_asset_class} funds` : ''}>
                      {r.score?.rank_in_cohort ? `${r.score.rank_in_cohort}/${r.score.cohort_scored}` : '–'}
                    </td>
                  )}
                  {hasScore && (
                    <td className="qs-l">
                      {r.score?.metrics?.length > 0 && (
                        <button type="button" className="qs-expander" onClick={() => toggleOpen(r.tr_code)} aria-expanded={isOpen} title="Weighting breakdown">{isOpen ? '▾' : '▸'}</button>
                      )}
                    </td>
                  )}
                  {hasSpark && <td className="qs-l"><Sparkline values={r.sparkline} /></td>}
                </tr>
                {hasScore && isOpen && (
                  <tr className="qs-details-row">
                    <td colSpan={nCols}>
                      <div className="qs-details">
                        <div className="qs-details-pillars">
                          {pillars.map((p, i) => {
                            const s = r.score.pillars?.[p];
                            return (
                              <span key={p} className="qs-pillar-chip" style={{ borderLeftColor: pillarColour(i) }}>
                                {p}: <b>{s?.score === null || s?.score === undefined ? '–' : s.score.toFixed(1)}</b>
                                <span className="qs-muted"> contributes {(s?.contribution ?? 0).toFixed(1)}</span>
                              </span>
                            );
                          })}
                          <span className="qs-muted">metrics used {r.score.metrics_used}/{r.score.metrics_total}</span>
                        </div>
                        <table className="qs-details-table">
                          <thead><tr><th className="qs-l">Pillar</th><th className="qs-l">Metric</th><th>Value</th><th className="qs-l">Percentile in SAC</th><th>Contribution</th></tr></thead>
                          <tbody>
                            {r.score.metrics.map((d) => (
                              <tr key={d.key}>
                                <td className="qs-l qs-dim">{d.pillar}</td>
                                <td className="qs-l">{d.label} · {d.period}{d.rolling ? ` roll ${d.rolling}m ${d.summary}` : ''}</td>
                                <td className="qs-num">{d.value === null ? <span className="qs-null" title={REASON_TEXT[d.reason] || d.reason}>n/a</span> : fmtValue(d.value, d.unit)}</td>
                                <td className="qs-l"><PercentileBar pct={d.percentile} thin={false} n={0} /></td>
                                <td className="qs-num">{d.contribution === null || d.contribution === undefined ? '–' : d.contribution.toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={nCols} className="qs-empty">No funds match.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
